import { useLayoutEffect } from 'react';
import { usePrevious } from 'react-use';

import { visitPaths } from '~libs.desktop.shared.paths';

import { PatientGroupFilterParam } from './PatientGroupField.types';
import { FILTER_SEPARATOR, mapPatientGroupFilterToPatientGroupIds } from './PatientGroupField.utils';

const STORAGE_PREFIX = 'egabinet-desktop:';
const PATIENT_GROUP_FILTER_STORAGE_KEY = `${STORAGE_PREFIX}patientGroupFilter`;

type SetFilter = (filter: PatientGroupFilterParam) => void;
type RemoveFilter = VoidFunction;
type GetStoredPatientGroupIds = () => number[];

type PatientGroupFilterStorageReturnFn = {
   setFilter: SetFilter;
   removeFilter: RemoveFilter;
   getStoredPatientGroupIds: GetStoredPatientGroupIds;
};

export const patientGroupFilterStorage: PatientGroupFilterStorageReturnFn = (() => {
   const removeFilter: RemoveFilter = () => window.sessionStorage.removeItem(PATIENT_GROUP_FILTER_STORAGE_KEY);
   const setFilter: SetFilter = (filter) => {
      if (filter === '') return removeFilter();

      return window.sessionStorage.setItem(PATIENT_GROUP_FILTER_STORAGE_KEY, filter);
   };

   const getStoredPatientGroupIds: GetStoredPatientGroupIds = () =>
      mapPatientGroupFilterToPatientGroupIds(window.sessionStorage.getItem(PATIENT_GROUP_FILTER_STORAGE_KEY));

   return {
      getStoredPatientGroupIds: getStoredPatientGroupIds,
      removeFilter: removeFilter,
      setFilter: setFilter,
   };
})();

export const useSyncPatientGroupIdsFilterParamWithStorage = (
   searchParams: URLSearchParams,
   onSync: (searchParams: URLSearchParams) => void
): void => {
   const patientGroupIdsFilterParam = searchParams.get(visitPaths.PATIENT_GROUP_SEARCH_PARAM);
   const previousPatientGroupIdsFilterParam = usePrevious(patientGroupIdsFilterParam);
   const memoizedPatientGroupIdsFilter = patientGroupFilterStorage.getStoredPatientGroupIds();

   useLayoutEffect(() => {
      if (patientGroupIdsFilterParam && patientGroupIdsFilterParam !== previousPatientGroupIdsFilterParam) {
         const patientGroupIdsSearchParam = patientGroupIdsFilterParam.split(FILTER_SEPARATOR).map(Number);
         const patientGroupIdsValid = patientGroupIdsSearchParam.every((id) => !isNaN(id));

         if (!patientGroupIdsValid) {
            searchParams.delete(visitPaths.PATIENT_GROUP_SEARCH_PARAM);
            onSync(searchParams);
         }

         if (patientGroupIdsValid) {
            patientGroupFilterStorage.setFilter(patientGroupIdsFilterParam);
         }
      }

      if (memoizedPatientGroupIdsFilter.length) {
         const patientGroupIdsSearchParam = patientGroupIdsFilterParam?.split(FILTER_SEPARATOR);
         const memoizedAsSet = new Set(memoizedPatientGroupIdsFilter);

         if (!patientGroupIdsSearchParam || patientGroupIdsSearchParam.some((id) => !memoizedAsSet.has(Number(id)))) {
            const newParamValue = Array.from(memoizedAsSet).join(FILTER_SEPARATOR);

            searchParams.set(visitPaths.PATIENT_GROUP_SEARCH_PARAM, newParamValue);
            onSync(searchParams);
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [patientGroupIdsFilterParam, memoizedPatientGroupIdsFilter]);
};
